/**
 * Shared correspondent-screen chips + formatters.
 *
 * These started life inside pages/Drilldown.js; they moved here so the agreement
 * document modal can render the same status pill as the version-history grid
 * without importing back into the page (which would be circular).
 */
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";

import { status as statusColors } from "../../../theme/colors";
import { T } from "../../SupportScreen/components/supportUi";

export const STATUS_TONE = {
  "Final Executed": { bg: "#ecfdf5", fg: "#047857" },
  Approved: { bg: "#eff6ff", fg: "#1d4ed8" },
  "Under Review": { bg: "#fffbeb", fg: "#b45309" },
  Proposed: { bg: "#f1f5f9", fg: T.slate },
};

// One pill geometry for both chips. `height` + `lineHeight: 1` matter: inside an
// AG Grid cell the inherited line-height is ~the row height, which stretches an
// inline-flex pill to nearly the full row. Pinning the box keeps it chip-sized so
// the cell's flex centering actually reads as centered.
const PILL_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  height: "20px",
  lineHeight: 1,
  borderRadius: "999px",
  px: "8px",
  fontSize: "11px",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

export function StatusChip({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.Proposed;
  return (
    <Box
      component="span"
      sx={{ ...PILL_SX, background: tone.bg, color: tone.fg }}
    >
      {status || "—"}
    </Box>
  );
}

// `outlined` adds a tinted 1px border in the chip's own color — for chips
// that sit on the gray page background (the drilldown header), where the pale
// fill alone washes out. Same treatment TierChip uses.
const outlinedSx = (fg) => ({ border: `1px solid ${alpha(fg, 0.35)}`, height: "24px", px: "9px" });

export function SoftBadge({ tone = "slate", icon: Icon, outlined, children }) {
  const TONES = {
    green: { bg: "#ecfdf5", fg: "#047857" },
    amber: { bg: "#fffbeb", fg: "#b45309" },
    slate: { bg: "#f1f5f9", fg: T.slate },
    // Tokenised (theme/colors.js `status.info` = steel) rather than another
    // hand-typed hex — used for "in progress / invited" states.
    steel: { bg: statusColors.info.bg, fg: statusColors.info.fg },
  };
  const t = TONES[tone] || TONES.slate;
  return (
    <Box
      component="span"
      sx={{
        ...PILL_SX,
        gap: "4px",
        background: t.bg,
        color: t.fg,
        ...(outlined ? outlinedSx(t.fg) : {}),
      }}
    >
      {Icon && <Icon sx={{ fontSize: 12 }} />}
      {children}
    </Box>
  );
}

export const fmtSize = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Where the correspondent relationship stands (`onboardingStatus`). The most
// load-bearing fact about a partner — an Inactive one should never read as a
// live counterparty — so it gets the same pill everywhere it appears: the list
// grid, the drilldown header, the Profile tab. Tones come from the status
// tokens rather than hand-typed hex.
export const ONBOARDING_TONE = {
  Active: statusColors.success,
  Onboarding: statusColors.warning,
  Inactive: statusColors.error,
};

/**
 * `sx` is merged last so a surface can match its own controls — the Profile
 * tab's status preview sits beside a `size="small"` select and takes that
 * control's 40px height and 6px radius, so the pair reads as one row rather
 * than a small pill floating next to a full-height input.
 */
export function OnboardingStatusChip({
  status,
  placeholder = "No status",
  outlined,
  sx,
}) {
  const tone = ONBOARDING_TONE[status] || statusColors.neutral;
  return (
    <Box
      component="span"
      sx={{
        ...PILL_SX,
        background: tone.bg,
        color: tone.fg,
        ...(outlined ? outlinedSx(tone.fg) : {}),
        ...sx,
      }}
    >
      {status || placeholder}
    </Box>
  );
}

// Pricing fields as a person reads them — for the "last change" strip. The
// deprecated single-rate fields keep their raw key (they only appear on legacy
// records). pricingTier mirrors dscr_pricing_tier, so both read "DSCR tier".
export const PRICING_FIELD_LABELS = {
  dscr_pricing_tier: "DSCR tier",
  pricingTier: "DSCR tier",
  dscr_additional_margin: "Additional margin",
  dscr_margin_override_points: "Margin override",
  rtl_pricing_tier: "RTL buy rate",
  rtl_additional_spread_pct: "RTL spread overlay",
  rtl_spread_overlay_note: "Overlay note",
  rtl_pricing_spreads: "Per-product overrides",
  buyRate: "Buy rate",
  tfFee: "Table-funding fee",
  max_lock_days: "Max lock days",
  lock_extension_fee_bps_per_day: "Extension fee",
  lock_extension_fee_options: "Extension fee presets",
};
export const pricingFieldLabel = (f) => PRICING_FIELD_LABELS[f] || f;

// Who / what / when of the last pricing edit. The backend denormalizes it onto
// the record as `pricingLastChange` {at, by, reason, fields[]}; records last
// edited before that existed fall back to the newest audit-trail batch (the
// entries sharing the latest changedDateTime).
export const lastPricingChangeOf = (detail) => {
  const lc = detail?.pricingLastChange;
  if (lc?.at) return lc;
  const trail = detail?.pricingAuditTrail ?? [];
  if (!trail.length) return null;
  const last = trail[trail.length - 1];
  const batch = trail.filter(
    (a) =>
      a.changedDateTime === last.changedDateTime &&
      a.changedBy === last.changedBy,
  );
  return {
    at: last.changedDateTime,
    by: last.changedBy,
    reason: last.reason,
    fields: batch.map((a) => a.field),
  };
};


// "DSCR tier, RTL spread overlay" — the change's fields, readable and deduped.
export const pricingChangeFields = (lc) =>
  [...new Set((lc?.fields || []).map(pricingFieldLabel))].join(", ");
